import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import BatchDetailTable from './components/BatchDetailTable';
import { BatchStatusBadge } from './components/StatusBadge';
import { formatCurrency, formatDate } from './helpers/csvValidation';
import { useNfseContext } from './NfseContext';

const NfseLoteDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedBatch, loadBatch, loading, clearSelectedBatch } =
    useNfseContext();

  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (id) {
      loadBatch(parseInt(id));
    }

    return () => {
      clearSelectedBatch();
    };
  }, [id, loadBatch, clearSelectedBatch]);

  const handleRefresh = useCallback(() => {
    if (id) {
      loadBatch(parseInt(id));
    }
  }, [id, loadBatch]);

  if (loading) {
    return (
      <Stack h="100vh" align="center" justify="center" gap="1rem">
        <Spinner size="xl" color="yooga.500" />
        <Text>Carregando lote...</Text>
      </Stack>
    );
  }

  if (!selectedBatch) {
    return (
      <Stack h="100vh" align="center" justify="center" gap="1rem">
        <Text color="gray.500">Lote não encontrado</Text>
        <Button as={RouterLink} to="/financeiro" colorScheme="yooga">
          Voltar para lista
        </Button>
      </Stack>
    );
  }

  const isProcessing = selectedBatch.status === 'PROCESSING';

  return (
    <Stack
      minW={{ lg: '740px' }}
      w={{ base: '90%', lg: '95%' }}
      m="auto"
      p={{ base: '2rem 1rem', lg: '4rem' }}
      gap="1.5rem"
    >
      <HStack spacing="0.5rem">
        <IconButton
          size="sm"
          variant="ghost"
          icon={<IoArrowBackOutline />}
          onClick={() => navigate('/financeiro')}
          aria-label="Voltar"
        />
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/financeiro">
              NFS-e
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Lote #{selectedBatch.id}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </HStack>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack gap="1.5rem">
          {/* Form Header */}
          <Grid templateColumns="repeat(12, 1fr)" gap="1rem" alignItems="end">
            <GridItem colSpan={{ base: 6, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm">ID do Lote</FormLabel>
                <Input
                  size="sm"
                  value={selectedBatch.id}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm">Data de Referência</FormLabel>
                <Input
                  size="sm"
                  value={formatDate(selectedBatch.referenceDate)}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm">Quantidade</FormLabel>
                <Input
                  size="sm"
                  value={selectedBatch.quantity}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm">Valor Total</FormLabel>
                <Input
                  size="sm"
                  value={formatCurrency(selectedBatch.totalValue)}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm">Status</FormLabel>
                <HStack h="2rem" align="center">
                  <BatchStatusBadge status={selectedBatch.status} />
                  {isProcessing}
                </HStack>
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 6, md: 2 }}>
              <HStack justify="flex-end" h="100%" align="end">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="yooga"
                  onClick={handleRefresh}
                  isLoading={loading}
                >
                  Atualizar
                </Button>
              </HStack>
            </GridItem>
          </Grid>

          {/* Documents Table */}
          <BatchDetailTable records={selectedBatch.records || []} />
        </Stack>
      </Box>
    </Stack>
  );
};

export default NfseLoteDetail;
